"use client";

import { useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { PermissionAction } from "@/lib/api/types/enterprise";
import { setRouteFlash } from "@/lib/navigation/route-flash";
import { NO_ACCESS_ROUTE, resolveAccessibleRoute } from "@/lib/navigation/sidebar-nav";
import { useModulePermission } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

export function ModuleRoute({
  moduleKey,
  action = "view",
  fallback = "/overview",
  superAdminOnly = false,
  children,
}: {
  moduleKey: string;
  action?: PermissionAction;
  fallback?: string;
  superAdminOnly?: boolean;
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const { allowed, isLoading, permissionMap } = useModulePermission(moduleKey, action);
  const roleAllowed = !superAdminOnly || user?.role === "super_admin";

  const fallbackRoute = useMemo(
    () => resolveAccessibleRoute(user, permissionMap, fallback),
    [fallback, permissionMap, user],
  );

  useEffect(() => {
    if (!user || isLoading || (allowed && roleAllowed)) {
      return;
    }

    let previousPath: string | null = null;
    if (typeof window !== "undefined" && document.referrer) {
      try {
        const referrer = new URL(document.referrer);
        if (referrer.origin === window.location.origin) {
          previousPath = `${referrer.pathname}${referrer.search}`;
        }
      } catch {
        previousPath = null;
      }
    }

    const canUsePrevious =
      previousPath &&
      previousPath !== location.pathname &&
      previousPath !== "/login" &&
      resolveAccessibleRoute(user, permissionMap, previousPath) === previousPath;
    const redirectTo = canUsePrevious && previousPath ? previousPath : (fallbackRoute ?? fallback);

    setRouteFlash(
      redirectTo === NO_ACCESS_ROUTE
        ? "Your account is active, but no module access has been assigned yet."
        : "You don't have permission to access that page.",
    );
    navigate(redirectTo, { replace: true });
  }, [allowed, fallbackRoute, isLoading, location.pathname, navigate, permissionMap, roleAllowed, user]);

  if (!user) {
    return null;
  }

  if (isLoading && user.role !== "super_admin") {
    return <div className="p-8 text-sm text-muted">Checking access...</div>;
  }

  if (!allowed || !roleAllowed) {
    return <div className="p-8 text-sm text-muted">Redirecting to an available page...</div>;
  }

  return <>{children}</>;
}
